import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserRoundCog } from 'lucide-react'
import Button from '../../components/Button'
import PageLoader from '../../components/PageLoader'
import { resolveLegacyRoute } from '../../app/legacyRoutes'
import { useResource } from '../../lib/useResource'
import { getProfileRedirect } from './systemService'
import { systemPageStrings as S } from './strings'
import './account.css'

/**
 * `/Redirect` — the interstitial a user meets the first time they sign in with an
 * incomplete profile. It says why they are being moved and then moves them.
 *
 * The server owns all three decisions: the sentence, the destination, and how long to
 * leave the message up (`RedirectDelaySeconds`, 8 by default). None of them are guessed
 * here — a countdown this app invented would drift from the one the product documents.
 *
 * The destination arrives as a legacy controller name (`UserProfile`, `Courses`), so it
 * goes through the same route map as any other legacy URL and lands on the React screen
 * that replaced it.
 *
 * `SessionGate` sends a gated user straight to their profile without passing through
 * here; this screen is for the URL itself, which stays live (R1).
 */
export default function RedirectPage() {
  const navigate = useNavigate()
  const { data, loading } = useResource((signal) => getProfileRedirect(signal), [])

  const target = data?.redirectTo ? resolveLegacyRoute(`/${data.redirectTo}`) : null

  useEffect(() => {
    if (!target) return undefined

    const timer = setTimeout(() => navigate(target, { replace: true }), (data.delaySeconds ?? 8) * 1000)
    return () => clearTimeout(timer)
  }, [target, data?.delaySeconds, navigate])

  if (loading) {
    return (
      <main className="acct-system">
        <section className="acct-system__card">
          <PageLoader />
        </section>
      </main>
    )
  }

  return (
    <main className="acct-system">
      <section className="acct-system__card">
        <span className="acct-system__icon acct-system__icon--warning" aria-hidden="true">
          <UserRoundCog size={34} strokeWidth={1.5} />
        </span>

        <h1 className="acct-system__title">
          <bdi>{data?.title || S.redirect.title}</bdi>
        </h1>

        <p className="acct-system__line">
          <bdi>{data?.message || S.redirect.message}</bdi>
        </p>

        {/* The wait is a courtesy, not a gate — anyone who has read it can go now. */}
        <div className="acct-system__actions">
          <Button onClick={() => navigate(target ?? '/portal/profile', { replace: true })}>
            {S.redirect.go}
          </Button>
        </div>
      </section>
    </main>
  )
}
